import React, { useState, useEffect } from 'react'
import useAxios from 'axios-hooks'
import { getAuthHeader } from 'utils/auth'
import { freeSearch } from 'utils/search'
import { Modal, Form, Input } from 'antd'
import { PageAlert, notificationToast } from 'components'
import AccountsDomainsTop from 'views/Administration/Account/AccountDomains/AccountsDomainsTop'
import DomainList from 'views/Administration/Account/AccountDomains/DomainList'
import DomainForm from 'views/Administration/Account/AccountDomains/DomainForm'
import FormFooter from 'views/ScanSettings/components/ScanFilterBar/FormFooter'
import styled from '@emotion/styled'

const Search = Input.Search

const formKeys = {
  DOMAIN: 'domain',
  UREMEDIATE: 'uremediate',
  SSL: 'ssl',
  UREMEDIATE_DOMAIN: 'uremediate_domain'
}

const SEARCH_MAPPING = {
  domain: 'domain'
}

const SearchDomains = styled(Search)({
  width: 380,
  display: 'block',
  margin: '10px 0'
})

export const DomainsContext = React.createContext()

const AccountDomains = ({ form, name, id }) => {
  const [domainsData, setDomainsData] = useState([])
  const [currentEditDomain, setCurrentEditDomain] = useState(null)
  const [domainFormVisible, setDomainFormVisible] = useState(false)

  // domain list
  const [{ data: listDomainsResponse, loading: listDomainsLoading, error: listDomainsError }, getDomains] = useAxios({
    url: `/account/${id}/domains`,
    method: 'GET',
    headers: getAuthHeader(),
    validateStatus: false
  })

  // ADD Domain
  let [{ data: newDomainData, loading: addingDomain, error: addDomainError }, addNewDomain] = useAxios(
    {
      url: `/account/${id}/domains`,
      method: 'POST',
      headers: getAuthHeader(),
      validateStatus: false
    },
    { manual: true }
  )
  // UPDATE Domain
  let [{ data: updateDomainData, loading: updatingDomain, error: updateDomainError }, updateDomain] = useAxios(
    {
      url: `/account/${id}/domains`,
      method: 'PUT',
      headers: getAuthHeader(),
      validateStatus: false
    },
    { manual: true }
  )

  // DELETE Domain
  let [{ data: deleteDomainData, loading: deleteDomainLoading, error: deleteDomainError }, deleteDomain] = useAxios(
    {
      method: 'DELETE',
      headers: getAuthHeader(),
      validateStatus: false
    },
    { manual: true }
  )

  const openNotification = (message, errors) => {
    if (!errors) {
      notificationToast({
        message: 'Success',
        description: message,
        icontype: 'check'
      })
    } else {
      notificationToast({
        message: 'Error',
        description: `${message} ${errors.join()}`,
        icontype: 'close-circle'
      })
    }
  }

  const onNewDomain = () => {
    setDomainFormVisible(true)
  }

  const onSearch = term => {
    if (term && term.length) setDomainsData(freeSearch(domainsData, term, SEARCH_MAPPING))
    else {
      setDomainsData(listDomainsResponse)
    }
  }

  // close Domain form
  const closeDomainForm = () => {
    if (currentEditDomain) {
      setCurrentEditDomain(null)
    }
    setDomainFormVisible(false)
  }

  // Edit Domain
  const onEdit = domain => {
    setCurrentEditDomain(domain)
    setDomainFormVisible(true)
  }

  // Delete Project
  const onDelete = domainId => {
    deleteDomain({
      url: `/account/${id}/domains/${domainId}`
    })
  }

  const onDomainFormAfterClose = () => {
    form.resetFields()
  }

  const onEditSave = () => {
    form.validateFields((errors, values) => {
      if (!errors) {
        let updatesPayload = {
          id: currentEditDomain.id,
          domain: values[formKeys.DOMAIN],
          ssl: values[formKeys.SSL],
          uremediate: values[formKeys.UREMEDIATE],
          uremediate_domain: values[formKeys.UREMEDIATE] ? values[formKeys.UREMEDIATE_DOMAIN] : null
        }

        updateDomain({
          data: updatesPayload
        })
      }
    })
  }

  // New Domain
  const addDomain = () => {
    form.validateFields((errors, values) => {
      if (!errors) {
        addNewDomain({
          data: {
            domain: values[formKeys.DOMAIN],
            ssl: values[formKeys.SSL],
            uremediate: values[formKeys.UREMEDIATE],
            uremediate_domain: values[formKeys.UREMEDIATE_DOMAIN]
          }
        })
      }
    })
  }

  // for list of domains
  useEffect(() => {
    if (listDomainsResponse && listDomainsResponse.length) {
      setDomainsData(listDomainsResponse)
    }
  }, [listDomainsResponse])

  // for creation of new domain
  useEffect(() => {
    if (newDomainData) {
      if (newDomainData.Errors) {
        const errorMessage = newDomainData.Errors
        openNotification('Could not add domain.', errorMessage)
      } else {
        closeDomainForm()
        openNotification('Domain was created successfully.')
        getDomains()
      }
    } else if (addDomainError) {
      const errorMessage = addDomainError?.response?.data?.Errors
      openNotification('Could not create domain.', errorMessage)
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [newDomainData, addDomainError, getDomains])

  // for domain update request
  useEffect(() => {
    if (updateDomainData) {
      if (updateDomainData.Errors) {
        const errorMessage = updateDomainData.Errors
        openNotification('Could not update domain.', errorMessage)
      } else {
        closeDomainForm()
        openNotification('Domain was edited successfully.')
        getDomains()
      }
    } else if (updateDomainError) {
      const errorMessage = updateDomainError?.response?.data?.Errors
      openNotification('Could not update domain.', errorMessage)
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [updateDomainData, updateDomainError, getDomains])

  // for domain delete request
  useEffect(() => {
    if (deleteDomainData) {
      openNotification('Domain was successfully deleted.')
      getDomains()
    } else if (deleteDomainError) {
      const errorMessage = deleteDomainError?.response?.data?.Errors
      openNotification('Could not delete domain.', errorMessage)
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [deleteDomainData, deleteDomainError, getDomains])

  return (
    <DomainsContext.Provider value={{ onEdit, onDelete }}>
      {listDomainsError && <PageAlert message={listDomainsError} />}
      <AccountsDomainsTop accountName={name} onNewDomain={onNewDomain} />
      <SearchDomains placeholder="Search domain" onChange={event => onSearch(event.target.value)} allowClear />
      <DomainList data={domainsData} loading={listDomainsLoading} setDomainFormVisible={setDomainFormVisible} />
      <Modal
        title={currentEditDomain ? `Edit Domain: ${currentEditDomain.domain}` : 'New Domain'}
        width={380}
        visible={domainFormVisible}
        closable={false}
        maskClosable={false}
        destroyOnClose={true}
        afterClose={onDomainFormAfterClose}
        footer={
          <FormFooter
            onOK={currentEditDomain ? onEditSave : addDomain}
            onClose={closeDomainForm}
            okText="SUBMIT"
            confirmLoading={updatingDomain || addingDomain}
          />
        }
      >
        <DomainForm domain={currentEditDomain} form={form} formKeys={formKeys} />
      </Modal>
    </DomainsContext.Provider>
  )
}

export default Form.create({})(AccountDomains)
